import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AddIcon from "@mui/icons-material/Add";
import baseUrl from "../../../api/baseUrl";
import CommonLayout from "../../../layouts/commonLayout/CommonLayout";
import {
  AddDentistSessionModal,
  type DentistSessionFormRow,
} from "../../setting/managetime/components/AddDentistSessionModal";

type DentistOption = { _id: string; name: string };

type SessionRow = {
  dentistName: string;
  day: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  doctorId: string;
  roomId: string;
};

type RoomOption = {
  _id: string;
  roomNumber: string;
  roomName: string;
  doctorsCount: number;
};

type GeneralSession = {
  sessionName: string;
  startTime: string;
  endTime: string;
};

type RoomSessionRow = {
  key: string;
  roomId: string;
  roomNo: string;
  roomName: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  day: string;
  allocated: SessionRow[];
};

function todayWeekday(): string {
  return new Date().toLocaleDateString(undefined, { weekday: "long" });
}

function ensureDrPrefix(name: string): string {
  const n = String(name ?? "").trim();
  if (!n) return "";
  if (/^dr\.?\s+/i.test(n)) return n.replace(/^dr\.?\s+/i, "Dr. ");
  return `Dr. ${n}`;
}

function flexibleTimeToMinutes(raw: string): number {
  const str = String(raw ?? "").trim();
  if (!str) return NaN;
  const hhmm = str.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);

  const mer = str.toLowerCase().match(/\b(am|pm)\b/);
  const isPm = mer?.[1] === "pm";
  const isAm = mer?.[1] === "am";
  const cleaned = str.toLowerCase().replace(/\b(am|pm)\b/gi, "").trim();
  const m = cleaned.match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return NaN;
  let h = parseInt(m[1], 10);
  let min = parseInt(m[2] ?? "0", 10);
  if (Number.isNaN(h)) return NaN;
  if (Number.isNaN(min)) min = 0;
  min = Math.min(59, Math.max(0, min));
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  return h * 60 + min;
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return aStart < bEnd && bStart < aEnd;
}

function isUnassignedRoom(roomId: string): boolean {
  return !String(roomId ?? "").trim();
}

function mapSessions(raw: unknown): SessionRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x: unknown) => {
      const o = x as Record<string, unknown>;
      return {
        dentistName: ensureDrPrefix(String(o?.dentistName ?? "")),
        day: String(o?.day ?? "").trim(),
        sessionName: String(o?.sessionName ?? "").trim(),
        startTime: String(o?.startTime ?? "").trim(),
        endTime: String(o?.endTime ?? "").trim(),
        roomNo: String(o?.roomNo ?? "").trim(),
        doctorId: String(o?.doctorId ?? "").trim(),
        roomId: String(o?.roomId ?? "").trim(),
      };
    })
    .filter((r) => r.sessionName && r.day && r.doctorId);
}

function buildRoomSessionRows(
  daySessions: SessionRow[],
  rooms: RoomOption[],
  generalSessions: GeneralSession[],
  day: string
): RoomSessionRow[] {
  const roomById = new Map(rooms.map((r) => [r._id, r]));
  const groups = new Map<string, SessionRow[]>();

  for (const s of daySessions) {
    if (isUnassignedRoom(s.roomId)) continue;
    const key = `${s.roomId}__${s.sessionName}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }

  for (const gs of generalSessions) {
    const anyForSession = daySessions.some((s) => s.sessionName === gs.sessionName);
    if (!anyForSession) continue;
    for (const room of rooms) {
      const key = `${room._id}__${gs.sessionName}`;
      if (!groups.has(key)) groups.set(key, []);
    }
  }

  const rows: RoomSessionRow[] = [];

  for (const [key, allocated] of Array.from(groups.entries())) {
    const [roomId, sessionName] = key.split("__");
    const room = roomById.get(roomId);
    const gs = generalSessions.find((g) => g.sessionName === sessionName);

    let startTime = gs?.startTime ?? "";
    let endTime = gs?.endTime ?? "";
    if (allocated.length > 0) {
      let minStart = Infinity;
      let maxEnd = -Infinity;
      for (const a of allocated) {
        const sm = flexibleTimeToMinutes(a.startTime);
        const em = flexibleTimeToMinutes(a.endTime);
        if (Number.isFinite(sm)) minStart = Math.min(minStart, sm);
        if (Number.isFinite(em)) maxEnd = Math.max(maxEnd, em);
      }
      if (Number.isFinite(minStart) && minStart !== Infinity) {
        const hh = String(Math.floor(minStart / 60)).padStart(2, "0");
        const mm = String(minStart % 60).padStart(2, "0");
        startTime = `${hh}:${mm}`;
      }
      if (Number.isFinite(maxEnd) && maxEnd !== -Infinity) {
        const hh = String(Math.floor(maxEnd / 60)).padStart(2, "0");
        const mm = String(maxEnd % 60).padStart(2, "0");
        endTime = `${hh}:${mm}`;
      }
    }

    const hasAllocation = allocated.length > 0;
    const isGeneralSession = generalSessions.some((g) => g.sessionName === sessionName);
    if (!hasAllocation && !isGeneralSession) continue;
    if (!hasAllocation && isGeneralSession && !gs) continue;

    rows.push({
      key,
      roomId,
      roomNo: room?.roomNumber ?? allocated[0]?.roomNo ?? "—",
      roomName: room?.roomName ?? "",
      sessionName,
      startTime: startTime || allocated[0]?.startTime || "—",
      endTime: endTime || allocated[0]?.endTime || "—",
      day,
      allocated,
    });
  }

  rows.sort((a, b) => {
    const rn = a.roomNo.localeCompare(b.roomNo, undefined, { numeric: true });
    if (rn !== 0) return rn;
    return a.sessionName.localeCompare(b.sessionName);
  });

  return rows;
}

function RoomAllocation() {
  const day = todayWeekday();
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [generalSessions, setGeneralSessions] = useState<GeneralSession[]>([]);
  const [daySessions, setDaySessions] = useState<SessionRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [assignLoadingKey, setAssignLoadingKey] = useState<string | null>(null);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState("");
  /** Allocated doctor selected in a row before picking the replacement. */
  const [selectedInRow, setSelectedInRow] = useState<{
    rowKey: string;
    doctorId: string;
  } | null>(null);
  const [doctorContextMenu, setDoctorContextMenu] = useState<{
    x: number;
    y: number;
    row: RoomSessionRow;
    doctorId: string;
    doctorName: string;
  } | null>(null);
  const [isCreateSessionOpen, setIsCreateSessionOpen] = useState(false);
  const [createSessionPrefill, setCreateSessionPrefill] = useState<
    Partial<{
      day: string;
      sessionName: string;
      startTime: string;
      endTime: string;
      roomNo: string;
      roomId: string;
    }>
  >({});

  const refreshTable = useCallback(async () => {
    const dayStr = String(day ?? "").trim();
    if (!dayStr) {
      setDaySessions([]);
      setGeneralSessions([]);
      return;
    }
    setTableLoading(true);
    try {
      const [dentistsRes, roomsRes, sessionsRes, generalRes] = await Promise.all([
        baseUrl.get("/client/setting/dentistGeneralHours/getDentists"),
        baseUrl.get("/client/setting/clinicRoom/getRooms"),
        baseUrl.get("/client/setting/dentistGeneralHours/list", {
          params: { day: dayStr },
        }),
        baseUrl.get("/client/setting/dentistGeneralHours/getGeneralSessionNames", {
          params: { day: dayStr },
        }),
      ]);

      const rawDentists = dentistsRes.data?.data?.dentists;
      setDentists(
        Array.isArray(rawDentists)
          ? rawDentists
              .map((d: unknown) => {
                const o = d as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  name: ensureDrPrefix(String(o?.name ?? "")),
                };
              })
              .filter((d) => d._id && d.name)
          : []
      );

      const rawRooms = roomsRes.data?.data;
      setRooms(
        Array.isArray(rawRooms)
          ? rawRooms
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  roomNumber: String(o?.roomNumber ?? "").trim(),
                  roomName: String(o?.roomName ?? "").trim(),
                  doctorsCount: Number.isFinite(Number(o?.doctorsCount))
                    ? Number(o?.doctorsCount)
                    : 0,
                };
              })
              .filter((r) => r._id && r.roomNumber)
          : []
      );

      setDaySessions(mapSessions(sessionsRes.data?.data?.sessions));

      const rawGeneral = generalRes.data?.data?.sessions;
      setGeneralSessions(
        Array.isArray(rawGeneral)
          ? rawGeneral
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  sessionName: String(o?.sessionName ?? "").trim(),
                  startTime: String(o?.startTime ?? "").trim(),
                  endTime: String(o?.endTime ?? "").trim(),
                };
              })
              .filter((s) => s.sessionName)
          : []
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Failed to load room allocation"
      );
      setDaySessions([]);
      setGeneralSessions([]);
    } finally {
      setTableLoading(false);
    }
  }, [day]);

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  useEffect(() => {
    if (!doctorContextMenu) return;
    const close = () => setDoctorContextMenu(null);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [doctorContextMenu]);

  const tableRows = useMemo(
    () => buildRoomSessionRows(daySessions, rooms, generalSessions, day),
    [day, daySessions, generalSessions, rooms]
  );

  const sessionOptions = useMemo(() => {
    const byName = new Map<string, GeneralSession>();
    for (const gs of generalSessions) {
      if (gs.sessionName) byName.set(gs.sessionName, gs);
    }
    for (const row of tableRows) {
      if (row.sessionName && !byName.has(row.sessionName)) {
        byName.set(row.sessionName, {
          sessionName: row.sessionName,
          startTime: row.startTime,
          endTime: row.endTime,
        });
      }
    }
    return Array.from(byName.values()).sort((a, b) => {
      const aStart = flexibleTimeToMinutes(a.startTime);
      const bStart = flexibleTimeToMinutes(b.startTime);
      if (Number.isFinite(aStart) && Number.isFinite(bStart) && aStart !== bStart) {
        return aStart - bStart;
      }
      return a.sessionName.localeCompare(b.sessionName);
    });
  }, [generalSessions, tableRows]);

  const filteredTableRows = useMemo(() => {
    if (!selectedSession) return tableRows;
    return tableRows.filter((row) => row.sessionName === selectedSession);
  }, [selectedSession, tableRows]);

  useEffect(() => {
    if (!selectedSession) return;
    if (!sessionOptions.some((s) => s.sessionName === selectedSession)) {
      setSelectedSession("");
    }
  }, [selectedSession, sessionOptions]);

  const getDoctorSessionOnDay = useCallback(
    (doctorId: string, sessionName: string) => {
      return daySessions.find(
        (s) => s.doctorId === doctorId && s.sessionName === sessionName
      );
    },
    [daySessions]
  );

  const isAllocatedToRow = useCallback(
    (doctorId: string, row: RoomSessionRow) =>
      row.allocated.some((a) => a.doctorId === doctorId),
    []
  );

  const isRoomFullForRow = useCallback(
    (row: RoomSessionRow, excludeDoctorId?: string) => {
      const room = rooms.find((r) => r._id === row.roomId);
      const cap = room?.doctorsCount ?? 0;
      if (cap <= 0) return false;

      const startM = flexibleTimeToMinutes(row.startTime);
      const endM = flexibleTimeToMinutes(row.endTime);
      if (!Number.isFinite(startM) || !Number.isFinite(endM)) {
        return row.allocated.filter((a) => a.doctorId !== excludeDoctorId).length >= cap;
      }

      let used = 0;
      for (const existing of daySessions) {
        if (isUnassignedRoom(existing.roomId)) continue;
        if (existing.roomId !== row.roomId) continue;
        if (existing.doctorId === excludeDoctorId) continue;
        const eStart = flexibleTimeToMinutes(existing.startTime);
        const eEnd = flexibleTimeToMinutes(existing.endTime);
        if (!Number.isFinite(eStart) || !Number.isFinite(eEnd)) continue;
        if (!rangesOverlap(startM, endM, eStart, eEnd)) continue;
        used += 1;
      }
      return used >= cap;
    },
    [daySessions, rooms]
  );

  const rowSelection = useCallback(
    (row: RoomSessionRow) =>
      selectedInRow?.rowKey === row.key ? selectedInRow.doctorId : null,
    [selectedInRow]
  );

  const moveDoctorToRoom = async (
    session: SessionRow,
    targetRoomId: string,
    fromRoomId?: string | null
  ) => {
    const fromId =
      fromRoomId !== undefined
        ? fromRoomId
        : isUnassignedRoom(session.roomId)
          ? null
          : session.roomId;
    if (!isUnassignedRoom(fromId ?? "") && fromId === targetRoomId) return;
    await baseUrl.post("/client/setting/dentistGeneralHours/moveRoom", {
      doctorId: session.doctorId,
      ...(fromId ? { fromRoomId: fromId } : {}),
      toRoomId: targetRoomId,
      day: session.day,
      sessionName: session.sessionName,
    });
  };

  const unassignDoctorFromRoom = async (session: SessionRow, fromRoomId: string) => {
    await baseUrl.post("/client/setting/dentistGeneralHours/moveRoom", {
      doctorId: session.doctorId,
      fromRoomId,
      day: session.day,
      sessionName: session.sessionName,
    });
  };

  const replaceDoctorInRow = async (
    row: RoomSessionRow,
    fromDoctorId: string,
    toDoctorId: string
  ) => {
    const fromSession = row.allocated.find((a) => a.doctorId === fromDoctorId);
    const toSession = getDoctorSessionOnDay(toDoctorId, row.sessionName);
    if (!fromSession || !toSession) return;

    const targetRoomId = row.roomId;
    const sourceRoomId = toSession.roomId;
    if (!isUnassignedRoom(sourceRoomId) && sourceRoomId === targetRoomId) return;

    const loadingKey = `${row.key}__${fromDoctorId}__${toDoctorId}`;
    setAssignLoadingKey(loadingKey);
    try {
      if (isUnassignedRoom(sourceRoomId)) {
        await moveDoctorToRoom(toSession, targetRoomId);
        await unassignDoctorFromRoom(
          { ...fromSession, doctorId: fromDoctorId, day: row.day, sessionName: row.sessionName },
          targetRoomId
        );
        toast.success("Dentist assigned; previous dentist unassigned from room");
      } else {
        await baseUrl.post("/client/setting/dentistGeneralHours/swapRooms", {
          day: row.day,
          sessionName: row.sessionName,
          doctorAId: fromDoctorId,
          roomAId: targetRoomId,
          doctorBId: toDoctorId,
          roomBId: sourceRoomId,
        });
        toast.success("Dentists swapped between rooms");
      }
      setSelectedInRow(null);
      await refreshTable();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not change dentist"
      );
    } finally {
      setAssignLoadingKey(null);
    }
  };

  const assignDoctorToRow = async (doctorId: string, row: RoomSessionRow) => {
    const existing = getDoctorSessionOnDay(doctorId, row.sessionName);
    if (!existing) return;
    if (!isUnassignedRoom(existing.roomId) && existing.roomId === row.roomId) return;
    if (isRoomFullForRow(row, doctorId)) {
      toast.error("Room is full for this session.");
      return;
    }

    const loadingKey = `${row.key}__${doctorId}`;
    setAssignLoadingKey(loadingKey);
    try {
      await moveDoctorToRoom(existing, row.roomId);
      toast.success(
        isUnassignedRoom(existing.roomId)
          ? "Dentist assigned to this room"
          : "Dentist moved to this room"
      );
      setSelectedInRow(null);
      await refreshTable();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not assign room"
      );
    } finally {
      setAssignLoadingKey(null);
    }
  };

  const removeDoctorFromRoom = async (
    row: RoomSessionRow,
    doctorId: string,
    doctorName: string,
    options?: { skipConfirm?: boolean }
  ) => {
    setDoctorContextMenu(null);
    const allocated = row.allocated.find((a) => a.doctorId === doctorId);
    if (!allocated) return;

    if (
      !options?.skipConfirm &&
      !window.confirm(
        `Remove ${doctorName} from room ${row.roomNo} (${row.sessionName})? They will stay on the session without a room.`
      )
    ) {
      return;
    }

    const key = `${doctorId}__${row.roomId}__${row.day}__${row.sessionName}`;
    setDeleteLoadingKey(key);
    try {
      await unassignDoctorFromRoom(
        {
          ...allocated,
          doctorId,
          day: row.day,
          sessionName: row.sessionName,
        },
        row.roomId
      );
      toast.success("Dentist unassigned");
      setSelectedInRow(null);
      await refreshTable();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not remove dentist from room"
      );
    } finally {
      setDeleteLoadingKey(null);
    }
  };

  const onDoctorContextMenu = (
    e: React.MouseEvent,
    row: RoomSessionRow,
    doctorId: string,
    doctorName: string,
    allocatedHere: boolean
  ) => {
    if (!allocatedHere) return;
    e.preventDefault();
    e.stopPropagation();
    setDoctorContextMenu({ x: e.clientX, y: e.clientY, row, doctorId, doctorName });
    setSelectedInRow(null);
  };

  const onDoctorClick = (row: RoomSessionRow, doctorId: string) => {
    const allocatedHere = isAllocatedToRow(doctorId, row);
    const selectedId = rowSelection(row);

    if (allocatedHere) {
      if (selectedId === doctorId) {
        void removeDoctorFromRoom(
          row,
          doctorId,
          dentists.find((d) => d._id === doctorId)?.name ?? "Dentist",
          { skipConfirm: true }
        );
        return;
      }
      setSelectedInRow({ rowKey: row.key, doctorId });
      return;
    }

    const existing = getDoctorSessionOnDay(doctorId, row.sessionName);
    if (!existing) return;

    if (selectedId) {
      if (doctorId === selectedId) return;
      void replaceDoctorInRow(row, selectedId, doctorId);
      return;
    }

    if (row.allocated.length > 0) {
      toast.info("Select an allocated dentist first, then choose the new dentist.");
      return;
    }

    void assignDoctorToRow(doctorId, row);
  };

  const renderDoctorButtons = (row: RoomSessionRow) => {
    const selectedId = rowSelection(row);
    const swapping = Boolean(selectedId);

    return (
      <div className="flex flex-wrap justify-center gap-1.5 py-0.5">
        {dentists.map((d) => {
          const allocatedHere = isAllocatedToRow(d._id, row);
          const isSelected = selectedId === d._id;
          const existing = getDoctorSessionOnDay(d._id, row.sessionName);
          const hasSession = Boolean(existing);
          const isUnassigned =
            hasSession &&
            !allocatedHere &&
            isUnassignedRoom(existing?.roomId ?? "");
          const isInOtherRoom =
            hasSession &&
            !allocatedHere &&
            !isUnassignedRoom(existing?.roomId ?? "") &&
            existing?.roomId !== row.roomId;
          const roomFull =
            !swapping &&
            !allocatedHere &&
            hasSession &&
            isRoomFullForRow(row, d._id);
          const deleteKey = `${d._id}__${row.roomId}__${row.day}__${row.sessionName}`;
          const isDeleting = deleteLoadingKey === deleteKey;
          const isLoading =
            isDeleting || assignLoadingKey?.startsWith(`${row.key}__`);

          let disabled = false;
          let title = "Assign to this room";

          if (allocatedHere) {
            title = isSelected
              ? "Click again to unassign from this room"
              : "Click to select for replacement, or right-click to unassign";
          } else if (!hasSession) {
            disabled = true;
            title = "No session on this day";
          } else if (roomFull) {
            disabled = true;
            title = "Room is full";
          } else if (swapping) {
            title =
              !isUnassignedRoom(existing?.roomId ?? "") &&
              existing?.roomId !== row.roomId
                ? `Swap with ${d.name} (from room ${existing?.roomNo})`
                : isUnassignedRoom(existing?.roomId ?? "")
                  ? `Assign ${d.name} to this room`
                  : "Assign to this room";
          } else if (row.allocated.length > 0) {
            disabled = true;
            title = "Select an allocated dentist first";
          } else if (isUnassigned) {
            title = `Unassigned — assign to room ${row.roomNo}`;
          } else if (existing?.roomId !== row.roomId) {
            title = `Move from room ${existing?.roomNo}`;
          }

          return (
            <button
              key={d._id}
              type="button"
              disabled={disabled || Boolean(isLoading)}
              title={title}
              onClick={() => onDoctorClick(row, d._id)}
              onContextMenu={(e) =>
                onDoctorContextMenu(e, row, d._id, d.name, allocatedHere)
              }
              className={[
                "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm transition-all duration-150",
                isSelected
                  ? "border-primary bg-primary text-white shadow-primary/25 ring-2 ring-primary/30"
                  : allocatedHere
                    ? "border-primary bg-primary text-white shadow-primary/20 hover:bg-primaryLight"
                    : disabled
                      ? "cursor-not-allowed border-transparent bg-primary/[0.06] text-slate-400 shadow-none"
                      : isUnassigned
                        ? "border-amber-300/90 bg-amber-50 text-amber-900 hover:border-amber-400 hover:bg-amber-100"
                        : isInOtherRoom
                          ? "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                          : swapping
                            ? "border-primary/40 bg-primary/10 text-primary hover:border-primary/60 hover:bg-primary/15"
                            : "border-primary/20 bg-white text-slate-700 hover:border-primary/40 hover:bg-primary/[0.06]",
                isLoading ? "pointer-events-none opacity-50" : "",
              ].join(" ")}
            >
              <span>{d.name}</span>
              {isUnassigned ? (
                <span className="text-[9px] font-bold uppercase tracking-wide text-amber-700/90">
                  Unassigned
                </span>
              ) : isInOtherRoom && existing?.roomNo ? (
                <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  Rm {existing.roomNo}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  const scheduleSummary = useMemo(() => {
    const slots = filteredTableRows.length;
    const assigned = filteredTableRows.reduce((n, r) => n + r.allocated.length, 0);
    const unassigned = daySessions.filter(
      (s) => isUnassignedRoom(s.roomId) && s.day === day
    ).length;
    return { slots, assigned, unassigned };
  }, [filteredTableRows, daySessions, day]);

  const unassignedSessions = useMemo(
    () =>
      daySessions.filter((s) => isUnassignedRoom(s.roomId) && s.day === day),
    [daySessions, day]
  );

  const existingSessionsForModal = useMemo<DentistSessionFormRow[]>(
    () =>
      daySessions.map((s) => ({
        dentistName: s.dentistName,
        day: s.day,
        sessionName: s.sessionName,
        startTime: s.startTime,
        endTime: s.endTime,
        roomNo: s.roomNo,
        doctorId: s.doctorId,
        roomId: s.roomId,
      })),
    [daySessions]
  );

  const openCreateSession = (prefill?: typeof createSessionPrefill) => {
    setCreateSessionPrefill({ day, ...(prefill ?? {}) });
    setIsCreateSessionOpen(true);
  };

  const closeCreateSession = () => {
    setIsCreateSessionOpen(false);
    setCreateSessionPrefill({});
  };

  return (
    <CommonLayout>
      <div className="mx-auto mt-4 w-full max-w-6xl space-y-4 px-1 pb-6 sm:px-0">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MeetingRoomOutlinedIcon fontSize="small" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-slate-900">
                Room allocation
              </h1>
              <p className="mt-0.5 text-sm text-slate-500">
                Today · {day}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => openCreateSession()}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/20 bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-primary/20 transition-colors hover:bg-primaryLight"
            >
              <AddIcon sx={{ fontSize: 18 }} />
              Create Session
            </button>
            {!tableLoading &&
            (filteredTableRows.length > 0 || scheduleSummary.unassigned > 0) ? (
            <div className="flex shrink-0 gap-2 sm:justify-end">
              <div className="rounded-xl border border-primary/15 bg-primary/[0.04] px-3 py-2 text-center shadow-sm">
                <div className="text-lg font-semibold tabular-nums text-primary">
                  {scheduleSummary.slots}
                </div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-primary/70">
                  Slots
                </div>
              </div>
              <div className="rounded-xl border border-primary/20 bg-primary/10 px-3 py-2 text-center shadow-sm">
                <div className="text-lg font-semibold tabular-nums text-primary">
                  {scheduleSummary.assigned}
                </div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-primary/70">
                  Assigned
                </div>
              </div>
              <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-3 py-2 text-center shadow-sm">
                <div className="text-lg font-semibold tabular-nums text-amber-800">
                  {scheduleSummary.unassigned}
                </div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-amber-700/80">
                  Unassigned
                </div>
              </div>
            </div>
            ) : null}
          </div>
        </div>

        {/* Main panel */}
        <div className="overflow-hidden rounded-2xl border border-primary/15 bg-white shadow-[0_1px_3px_rgba(23,77,122,0.08)]">
          {sessionOptions.length > 0 ? (
            <div className="border-b border-primary/10 bg-white px-4 py-4 sm:px-6">
              <div className="mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Filter by session
                </span>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedInRow(null);
                    setSelectedSession("");
                  }}
                  className={[
                    "shrink-0 rounded-xl px-3 py-2 text-center transition-all",
                    !selectedSession
                      ? "bg-primary text-white shadow-md shadow-primary/20"
                      : "border border-primary/15 bg-white text-slate-600 hover:border-primary/30 hover:bg-primary/[0.04]",
                  ].join(" ")}
                >
                  <span className="block text-xs font-medium">All sessions</span>
                </button>
                {sessionOptions.map((session) => {
                  const active = selectedSession === session.sessionName;
                  const timeLabel =
                    session.startTime && session.endTime
                      ? `${session.startTime} – ${session.endTime}`
                      : null;
                  return (
                    <button
                      key={session.sessionName}
                      type="button"
                      onClick={() => {
                        setSelectedInRow(null);
                        setSelectedSession(session.sessionName);
                      }}
                      className={[
                        "shrink-0 rounded-xl px-3 py-2 text-center transition-all sm:min-w-[5.5rem]",
                        active
                          ? "bg-primary text-white shadow-md shadow-primary/20"
                          : "border border-primary/15 bg-white text-slate-600 hover:border-primary/30 hover:bg-primary/[0.04]",
                      ].join(" ")}
                    >
                      <span className="block text-xs font-medium">{session.sessionName}</span>
                      {timeLabel ? (
                        <span
                          className={[
                            "mt-0.5 block font-mono text-[10px] tabular-nums",
                            active ? "text-white/80" : "text-slate-400",
                          ].join(" ")}
                        >
                          {timeLabel}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-primary/10 bg-primary/[0.02] px-4 py-2.5 sm:px-6">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/70">
              Legend
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full bg-primary" />
              In room
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-primary/30" />
              Selected
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full border border-amber-300 bg-amber-50" />
              Unassigned
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full border border-slate-200 bg-slate-50" />
              In other room
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full bg-primary/20" />
              Unavailable
            </span>
          </div>

          {unassignedSessions.length > 0 ? (
            <div className="border-b border-amber-200/70 bg-amber-50/60 px-4 py-3 sm:px-6">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-amber-800/90">
                Unassigned dentists — click a room row below to assign
              </div>
              <div className="flex flex-wrap gap-2">
                {unassignedSessions.map((s) => (
                  <span
                    key={`${s.doctorId}__${s.sessionName}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/80 bg-white px-3 py-1 text-xs font-medium text-amber-900 shadow-sm"
                  >
                    {s.dentistName}
                    <span className="font-normal text-amber-700/80">· {s.sessionName}</span>
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800">
                      Unassigned
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-center">
              <thead className="sticky top-0 z-10 bg-primary/[0.06]">
                <tr className="border-b border-primary/15">
                  <th className="min-w-[100px] px-4 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-primary sm:px-6">
                    Room
                  </th>
                  <th className="min-w-[140px] px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Session
                  </th>
                  <th className="min-w-[110px] px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Time
                  </th>
                  <th className="min-w-[300px] px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-primary sm:px-6">
                    Dentists
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/10">
                {tableLoading ? (
                  <tr>
                    <td className="px-6 py-16" colSpan={4}>
                      <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
                        <span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
                        <span className="text-sm font-medium">Loading schedule…</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTableRows.length === 0 ? (
                  <tr>
                    <td className="px-6 py-14" colSpan={4}>
                      <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <ScheduleOutlinedIcon />
                        </div>
                        <p className="text-sm font-medium text-slate-700">
                          {tableRows.length > 0 && selectedSession
                            ? `No rooms for ${selectedSession}`
                            : `No schedule for today (${day})`}
                        </p>
                        <p className="text-sm text-slate-500">
                          {tableRows.length > 0 && selectedSession ? (
                            <button
                              type="button"
                              onClick={() => setSelectedSession("")}
                              className="font-medium text-primary hover:underline"
                            >
                              Show all sessions
                            </button>
                          ) : (
                            <>
                              Set up rooms and dentist hours under{" "}
                              <span className="font-medium text-slate-600">
                                Settings → Manage time
                              </span>
                              .
                            </>
                          )}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTableRows.map((row, index) => {
                    const swapping = Boolean(rowSelection(row));
                    const hasAssigned = row.allocated.length > 0;
                    return (
                      <tr
                        key={row.key}
                        className={[
                          "transition-colors",
                          index % 2 === 1 ? "bg-primary/[0.02]" : "bg-white",
                          swapping
                            ? "border-l-[3px] border-l-primary bg-primary/10"
                            : "border-l-[3px] border-l-transparent hover:bg-primary/[0.05]",
                        ].join(" ")}
                      >
                        <td className="border-r border-primary/5 px-4 py-4 text-left sm:px-6">
                          <span className="inline-flex items-center rounded-lg border border-primary/20 bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary">
                            {row.roomNo}
                          </span>
                          {row.roomName ? (
                            <div className="mt-1 text-xs text-primary/60">{row.roomName}</div>
                          ) : null}
                        </td>
                        <td className="border-r border-primary/5 px-3 py-4 text-left">
                          <span className="inline-block rounded-lg border border-primary/25 bg-primary/10 px-2.5 py-1 text-sm font-medium text-primary">
                            {row.sessionName}
                          </span>
                        </td>
                        <td className="border-r border-primary/5 px-3 py-4 text-left">
                          <span className="inline-flex items-center gap-1 rounded-lg border border-primary/15 bg-primary/5 px-2 py-1 font-mono text-xs tabular-nums text-primary">
                            <ScheduleOutlinedIcon className="!text-sm text-primary/70" />
                            {row.startTime}
                            <span className="text-primary/30">–</span>
                            {row.endTime}
                          </span>
                        </td>
                        <td
                          className={[
                            "px-4 py-3 sm:px-6",
                            hasAssigned ? "bg-primary/[0.03]" : "",
                          ].join(" ")}
                        >
                          <div className="flex flex-col items-center gap-2">
                            {renderDoctorButtons(row)}
                            {!hasAssigned ? (
                              <button
                                type="button"
                                onClick={() =>
                                  openCreateSession({
                                    sessionName: row.sessionName,
                                    startTime:
                                      row.startTime !== "—" ? row.startTime : "",
                                    endTime: row.endTime !== "—" ? row.endTime : "",
                                    roomNo: row.roomNo,
                                    roomId: row.roomId,
                                  })
                                }
                                className="inline-flex items-center gap-1 rounded-lg border border-dashed border-primary/30 px-2 py-1 text-[11px] font-medium text-primary hover:border-primary/50 hover:bg-primary/[0.04]"
                              >
                                <AddIcon sx={{ fontSize: 14 }} />
                                Add session
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {doctorContextMenu ? (
        <div
          className="fixed z-[1100] min-w-[180px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          style={{ left: doctorContextMenu.x, top: doctorContextMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
            onClick={() =>
              void removeDoctorFromRoom(
                doctorContextMenu.row,
                doctorContextMenu.doctorId,
                doctorContextMenu.doctorName
              )
            }
          >
            Remove from room
          </button>
        </div>
      ) : null}

      <AddDentistSessionModal
        open={isCreateSessionOpen}
        onClose={closeCreateSession}
        onSaved={refreshTable}
        fixedDay={day}
        existingSessions={existingSessionsForModal}
        initialValues={createSessionPrefill}
      />
    </CommonLayout>
  );
}

export default RoomAllocation;
